import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { useRecaptcha } from '@/hooks/useRecaptcha';
import { Building2, TrendingUp, ShieldCheck, Loader2, KeyRound, ShieldAlert, Lock, Zap, Clock, ArrowRight, Sparkles, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  InputOTP, InputOTPGroup, InputOTPSlot,
} from '../components/ui/input-otp';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { trimText } from '@/lib/passwordValidation';

const STATS = [
  { value: '500+', label: 'Loan products managed' },
  { value: '99.9%', label: 'Uptime SLA' },
  { value: '50+', label: 'NBFCs onboarded' },
];

function useZohoSalesIQ() {
  useEffect(() => {
    // Initialize and load Zoho SalesIQ only once
    if (!document.getElementById('zsiqscript')) {
      window.$zoho = window.$zoho || {};
      window.$zoho.salesiq = window.$zoho.salesiq || { ready: function () {} };
      const script = document.createElement('script');
      script.id = 'zsiqscript';
      script.src = 'https://salesiq.zohopublic.in/widget?wc=siq6e013309694f05fa62133f0434e585390883df48b3d16e0db4d28cde859cce1450ef7d6d9a92588a29f4b4ba617adc65';
      script.defer = true;
      document.body.appendChild(script);
    }
    // Show the widget when on login page
    const zohoSelectors = '[id^="zsiq"], [class*="zsiq"], [id^="siq"], .zls-sptwndw, .zsiq_float, .zsiq_theme1, .zsiq_cnt, .zls-wrapper';
    document.querySelectorAll(zohoSelectors).forEach(el => { el.style.display = ''; });

    return () => {
      // Hide (don't remove) all Zoho UI elements when leaving login page
      document.querySelectorAll(zohoSelectors).forEach(el => { el.style.display = 'none'; });
      document.querySelectorAll('iframe').forEach(iframe => {
        if (iframe.src?.includes('salesiq')) iframe.style.display = 'none';
      });
    };
  }, []);
}

function OTPStep({ otp, onOtpChange, onVerify, onResend, onBack, twoFAPending, verifying, loading, trustDevice, setTrustDevice }) {
  return (
    <div className="space-y-5" data-testid="otp-step">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
            <ShieldAlert className="h-4 w-4 text-blue-600" />
          </div>
          <h2 className="text-xl font-bold" data-testid="otp-heading">Verification Code</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Enter the 6-digit code sent to <strong>{twoFAPending.email}</strong>
        </p>
      </div>
      <div className="flex justify-center" data-testid="otp-input-group">
        <InputOTP maxLength={6} value={otp} onChange={onOtpChange}>
          <InputOTPGroup>
            <InputOTPSlot index={0} />
            <InputOTPSlot index={1} />
            <InputOTPSlot index={2} />
            <InputOTPSlot index={3} />
            <InputOTPSlot index={4} />
            <InputOTPSlot index={5} />
          </InputOTPGroup>
        </InputOTP>
      </div>
      <Button
        id="verify-otp-btn"
        className="w-full"
        onClick={onVerify}
        disabled={otp.length !== 6 || verifying}
        data-testid="verify-otp-btn"
      >
        {verifying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
        {verifying ? 'Verifying...' : 'Verify & Sign In'}
      </Button>
      <div className="flex items-center gap-2" data-testid="trust-device-checkbox">
        <Checkbox
          id="trust-device"
          checked={trustDevice}
          onCheckedChange={(checked) => setTrustDevice(checked === true)}
        />
        <Label htmlFor="trust-device" className="text-sm text-muted-foreground cursor-pointer">
          Trust this device for 30 days
        </Label>
      </div>
      <div className="flex items-center justify-between text-sm">
        <button
          type="button"
          className="text-primary hover:underline font-medium"
          onClick={onResend}
          disabled={loading}
          data-testid="resend-otp-btn"
        >
          {loading ? 'Sending...' : 'Resend Code'}
        </button>
        <button
          type="button"
          className="text-muted-foreground hover:underline"
          onClick={onBack}
          data-testid="back-to-login-btn"
        >
          Back to login
        </button>
      </div>
    </div>
  );
}

OTPStep.propTypes = {
  otp: PropTypes.string.isRequired,
  onOtpChange: PropTypes.func.isRequired,
  onVerify: PropTypes.func.isRequired,
  onResend: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  twoFAPending: PropTypes.shape({ email: PropTypes.string }).isRequired,
  verifying: PropTypes.bool,
  loading: PropTypes.bool,
  trustDevice: PropTypes.bool,
  setTrustDevice: PropTypes.func.isRequired,
};

function PasswordStep({ email, setEmail, password, setPassword, showPassword, setShowPassword, loading, onSubmit, onForgot }) {
  return (
    <>
      <div>
        <h2 className="text-xl font-bold" data-testid="login-heading">Welcome back</h2>
        <p className="text-sm text-muted-foreground mt-1">Sign in to your lending platform</p>
      </div>
      <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            onBlur={(e) => setEmail(trimText(e.target.value))}
            placeholder="admin@yourcompany.com" required className="bg-slate-50/50"
            data-testid="login-email-input"
          />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <button
              type="button"
              className="text-xs text-primary hover:underline font-medium"
              onClick={onForgot}
              data-testid="forgot-password-link"
            >
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <Input
              id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password" required className="bg-slate-50/50 pr-10"
              data-testid="login-password-input"
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
              onClick={() => setShowPassword((prev) => !prev)}
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              data-testid="toggle-password-visibility"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          {loading ? 'Signing in...' : 'Sign In'}
          {!loading && <ArrowRight className="h-4 w-4 ml-1" />}
        </Button>
      </form>
      <p className="text-center text-sm text-muted-foreground">
        Don&apos;t have an account?{' '}
        <Link to="/register" className="font-medium text-primary hover:underline" data-testid="login-register-link">Register your company</Link>
      </p>
    </>
  );
}

PasswordStep.propTypes = {
  email: PropTypes.string.isRequired,
  setEmail: PropTypes.func.isRequired,
  password: PropTypes.string.isRequired,
  setPassword: PropTypes.func.isRequired,
  showPassword: PropTypes.bool.isRequired,
  setShowPassword: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onForgot: PropTypes.func.isRequired,
};

export default function LoginPage() {
  useZohoSalesIQ();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [otp, setOtp] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);
  const { login, verify2FA, cancelTwoFA, twoFAPending } = useAuth();
  const { getToken } = useRecaptcha();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanEmail = trimText(email);
    const cleanPassword = trimText(password);
    setEmail(cleanEmail);
    if (!cleanEmail) {
      toast.error('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    try {
      const captcha_token = await getToken('login');
      const resp = await login(cleanEmail, cleanPassword, captcha_token);
      if (resp.requires_2fa) {
        toast.info('OTP sent to your registered email');
      } else {
        toast.success('Login successful');
        navigate('/dashboard');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) return;
    setVerifying(true);
    try {
      await verify2FA(twoFAPending.twofa_token, otp, trustDevice);
      toast.success('Login successful');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.message);
      setOtp('');
    } finally {
      setVerifying(false);
    }
  };

  const handleBack = () => {
    cancelTwoFA();
    setOtp('');
  };

  // Auto-submit when 6 digits entered
  const onOtpChange = (value) => {
    setOtp(value);
  };

  useEffect(() => {
    if (otp.length === 6) {
      const timer = setTimeout(() => {
        document.getElementById('verify-otp-btn')?.click();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [otp]);

  const handleResend = async () => {
    setLoading(true);
    try {
      const captcha_token = await getToken('login');
      await login(trimText(email), trimText(password), captcha_token);
      toast.info('New OTP sent to your email');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="login-page">
      {/* Top — Login Area */}
      <div className="flex flex-1 min-h-[600px] max-h-screen">
        {/* Left — Pitch */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white flex-col justify-between p-12">
        <div>
          <h1 className="text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>LMS</h1>
          <p className="text-slate-400 text-sm">Lending Management System</p>
        </div>
        <div className="space-y-8">
          <h2 className="text-4xl font-bold leading-tight" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Your lending<br />command center
          </h2>
          <p className="text-slate-300 text-lg max-w-md">
            End-to-end loan management, compliance automation, and real-time portfolio insights — all in one platform.
          </p>
          <div className="flex gap-8 pt-4">
            {STATS.map((s) => (
              <div key={s.label}>
                <p className="text-3xl font-bold">{s.value}</p>
                <p className="text-xs text-slate-400 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
          <div className="flex gap-4 pt-4">
            <div className="flex items-center gap-2 text-sm text-slate-300">
              <Building2 className="h-4 w-4" /> Multi-tenant
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-300">
              <ShieldCheck className="h-4 w-4" /> RBI Compliant
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-300">
              <TrendingUp className="h-4 w-4" /> Real-time Analytics
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-500">Secure. Compliant. Scalable.</p>
          <Link to="/features" className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-500 rounded-lg px-5 py-2.5 transition-colors shadow-lg shadow-blue-600/25">
            <Sparkles className="h-4 w-4" />
            Explore All Features
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      {/* Right — Login Form / OTP Step */}
      <div className="flex-1 flex flex-col justify-between p-0 relative overflow-hidden" style={{ background: 'linear-gradient(165deg, #f0f4ff 0%, #e8edf8 35%, #f8f9fc 100%)' }}>
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-[0.04] bg-blue-600" style={{ transform: 'translate(30%, -40%)' }} />
        <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full opacity-[0.03] bg-indigo-600" style={{ transform: 'translate(-30%, 40%)' }} />

        {/* Top accent bar */}
        <div className="h-1 w-full" style={{ background: 'linear-gradient(90deg, #1e40af, #3b82f6, #60a5fa)' }} />

        <div className="flex-1 flex items-center justify-center px-6 py-8">
          <div className="w-full max-w-sm space-y-5">
            <div className="lg:hidden text-center mb-4">
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>LMS</h1>
              <p className="text-xs text-muted-foreground">Lending Management System</p>
            </div>

            {/* Login card */}
            <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-6 space-y-5" style={{ boxShadow: '0 1px 3px 0 rgba(0,0,0,0.04), 0 4px 16px -2px rgba(0,0,0,0.06)' }}>
            {twoFAPending ? (
              <OTPStep
                otp={otp}
                onOtpChange={onOtpChange}
                onVerify={handleVerifyOTP}
                onResend={handleResend}
                onBack={handleBack}
                twoFAPending={twoFAPending}
                verifying={verifying}
                loading={loading}
                trustDevice={trustDevice}
                setTrustDevice={setTrustDevice}
              />
            ) : (
              <PasswordStep
                email={email}
                setEmail={setEmail}
                password={password}
                setPassword={setPassword}
                showPassword={showPassword}
                setShowPassword={setShowPassword}
                loading={loading}
                onSubmit={handleSubmit}
                onForgot={() => setForgotOpen(true)}
              />
            )}
            </div>

            {/* Trust indicators */}
            <div className="flex items-center justify-center gap-5 pt-1" data-testid="trust-indicators">
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                <Lock className="h-3 w-3" />
                <span>256-bit encrypted</span>
              </div>
              <div className="w-px h-3 bg-slate-300" />
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                <ShieldCheck className="h-3 w-3" />
                <span>RBI compliant</span>
              </div>
              <div className="w-px h-3 bg-slate-300" />
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                <Clock className="h-3 w-3" />
                <span>99.9% uptime</span>
              </div>
            </div>

            {/* Borrower portal link */}
            <div className="text-center pt-2">
              <p className="text-sm text-slate-500">
                Are you a borrower?{' '}
                <Link to="/portal/login" className="text-blue-600 hover:text-blue-700 font-medium hover:underline">
                  Sign in to the borrower portal
                </Link>
              </p>
            </div>
          </div>
        </div>

        {/* Bottom pitch strip */}
        <div className="border-t border-slate-200/60 bg-white/60 backdrop-blur-sm px-6 py-4">
          <div className="max-w-sm mx-auto space-y-3">
            <p className="text-xs font-semibold tracking-wider uppercase text-slate-400 text-center">Trusted by leading NBFCs</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-start gap-2.5 p-2.5 rounded-lg bg-white/80 border border-slate-100">
                <div className="w-6 h-6 rounded bg-emerald-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Zap className="h-3 w-3 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-700 leading-tight">Go live in days</p>
                  <p className="text-[10px] text-slate-400 leading-snug mt-0.5">Self-service onboarding with instant setup</p>
                </div>
              </div>
              <div className="flex items-start gap-2.5 p-2.5 rounded-lg bg-white/80 border border-slate-100">
                <div className="w-6 h-6 rounded bg-blue-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <ShieldCheck className="h-3 w-3 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-700 leading-tight">Full compliance</p>
                  <p className="text-[10px] text-slate-400 leading-snug mt-0.5">KFS, CIBIL, 2FA, cooling-off built in</p>
                </div>
              </div>
              <div className="flex items-start gap-2.5 p-2.5 rounded-lg bg-white/80 border border-slate-100">
                <div className="w-6 h-6 rounded bg-amber-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Building2 className="h-3 w-3 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-700 leading-tight">Multi-tenant SaaS</p>
                  <p className="text-[10px] text-slate-400 leading-snug mt-0.5">Complete data isolation per company</p>
                </div>
              </div>
              <div className="flex items-start gap-2.5 p-2.5 rounded-lg bg-white/80 border border-slate-100">
                <div className="w-6 h-6 rounded bg-violet-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <TrendingUp className="h-3 w-3 text-violet-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-700 leading-tight">End-to-end lifecycle</p>
                  <p className="text-[10px] text-slate-400 leading-snug mt-0.5">Origination to recovery in one platform</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Forgot Password Dialog */}
        <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <KeyRound className="h-5 w-5" /> Reset Your Password
              </DialogTitle>
              <DialogDescription>
                Password resets are managed by your organization's administrator.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="p-4 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-800">
                <p className="font-medium mb-1">How to reset your password:</p>
                <ol className="list-decimal list-inside space-y-1 text-blue-700">
                  <li>Contact your <strong>company administrator</strong> (Tenant Admin)</li>
                  <li>They can reset your password from the <strong>Team Members</strong> section</li>
                  <li>You'll receive a new temporary password to log in</li>
                </ol>
              </div>
              <div className="p-4 rounded-lg bg-slate-50 border text-sm">
                <p className="font-medium mb-1">Are you a Company Administrator?</p>
                <p className="text-muted-foreground">
                  If you're a Tenant Admin and have forgotten your password, please reach out to the{' '}
                  <strong>LMS platform support team</strong> at{' '}
                  <a href="mailto:support@lms.io" className="text-primary hover:underline">support@lms.io</a> for assistance.
                </p>
              </div>
              <Button variant="outline" className="w-full" onClick={() => setForgotOpen(false)} data-testid="forgot-close-btn">
                Got it, close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      </div>
    </div>
  );
}
